import { useParams } from 'react-router';
import { useState } from 'react';
import { cn } from '@/utils/utils.ts';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';
import { useGetControlPanelScriptsPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-control-panel-scripts.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DynamicForm } from '@/features/plants/components/plant-control/dynamic-form/dynamic-form.tsx';
import { useExecutePlantScriptMutation } from '../../hooks/mutations/[plantId]/use-execute-plant-script';

export const PlantControlScripts = ({ className }: { className?: string }) => {
  const { plantId } = useParams();
  const [selectedScript, setSelectedScript] = useState<string | undefined>(undefined);

  const { data, isLoading } = useGetControlPanelScriptsPlantQuery({ plantId });
  const scripts = data?.scripts ? data?.scripts : [];

  const isSelectedValid = scripts.some((script) => script.id === selectedScript);
  const activeScript = scripts.find(
    (script) =>
      typeof script.status === 'string' && script.status.toLowerCase().startsWith('active')
  );
  const fallbackScriptId = activeScript?.id ?? scripts[0]?.id;
  const resolvedSelectedScript = isSelectedValid ? selectedScript : fallbackScriptId;
  const selectedSchema = scripts?.find((script) => script.id === resolvedSelectedScript);

  const { mutate: executeScript, isPending } = useExecutePlantScriptMutation();

  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!selectedSchema?.id) return;

    executeScript({
      plantId,
      formId: selectedSchema?.id,
      formData: data,
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  return (
    <DashboardCard
      className={cn('w-full gap-4 overflow-hidden', className)}
      isLoading={isLoading}
    >
      <div className="flex justify-between gap-2 border-b">
        <div className="flex flex-col pb-4">
          <h3 className="font-semibold text-nowrap">Plant Scripts</h3>
          <LastUpdateTimestamp updatedAt={new Date().getTime()} />
        </div>
        <Select value={resolvedSelectedScript} onValueChange={setSelectedScript}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select script" />
          </SelectTrigger>
          <SelectContent>
            {Boolean(scripts?.length) &&
              scripts?.map((script) => (
                <SelectItem key={script.id} value={script.id}>
                  {script.title}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <DynamicForm schema={selectedSchema} onSubmit={handleSubmit} isSubmitting={isPending} />
    </DashboardCard>
  );
};
